import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Heart, DollarSign, Coffee, Gift, Loader2, CheckCircle, CreditCard, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AMOUNTS = [
  { id: 'small', value: 5, label: '$5', icon: Coffee, desc: 'Buy us a coffee' },
  { id: 'medium', value: 10, label: '$10', icon: Heart, desc: 'Show some love' },
  { id: 'large', value: 25, label: '$25', icon: Gift, desc: 'Big supporter' },
  { id: 'xlarge', value: 50, label: '$50', icon: CreditCard, desc: 'Super fan' },
];

export function Donation() {
  const { user } = useAuth();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [message, setMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  
  // Use refs for values that change frequently but shouldn't trigger button re-render
  const paypalContainerRef = useRef(null);
  const donorNameRef = useRef('');
  const messageRef = useRef('');
  const currentButtonsRef = useRef(null);
  const lastRenderedAmountRef = useRef(null);

  // Keep refs in sync with state
  useEffect(() => {
    donorNameRef.current = donorName;
  }, [donorName]);

  useEffect(() => {
    messageRef.current = message;
  }, [message]);

  // Calculate amount value
  const getAmountValue = useCallback(() => {
    if (selectedAmount === 'custom' && customAmount) {
      return parseFloat(customAmount);
    }
    if (selectedAmount) {
      const amt = AMOUNTS.find(a => a.id === selectedAmount);
      return amt?.value || 0;
    }
    return 0;
  }, [selectedAmount, customAmount]);

  // Fetch PayPal client ID
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/donations/packages`);
        if (response.ok) {
          const data = await response.json();
          setPaypalClientId(data.paypal_client_id);
        }
      } catch (err) {
        console.log('Failed to fetch payment config');
      }
    };
    fetchConfig();
  }, []);

  // Load PayPal SDK once
  useEffect(() => {
    if (!paypalClientId || paypalLoaded) return;
    
    // Check if script already exists
    const existingScript = document.querySelector(`script[src*="paypal.com/sdk/js"]`);
    if (existingScript) {
      if (window.paypal) {
        setPaypalLoaded(true);
      }
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&currency=USD`;
    script.async = true;
    script.onload = () => setPaypalLoaded(true);
    script.onerror = () => setError('Failed to load PayPal SDK');
    document.body.appendChild(script);
  }, [paypalClientId, paypalLoaded]);

  // Render PayPal buttons - only when amount changes significantly
  useEffect(() => {
    if (!paypalLoaded || !window.paypal || !paypalContainerRef.current) return;

    const amount = getAmountValue();
    const packageId = selectedAmount === 'custom' ? 'custom' : selectedAmount;

    // Don't render if amount is invalid
    if (!packageId || !amount || amount < 1) {
      // Clear container if amount becomes invalid
      if (paypalContainerRef.current) {
        paypalContainerRef.current.innerHTML = '';
      }
      lastRenderedAmountRef.current = null;
      currentButtonsRef.current = null;
      return;
    }

    // Skip re-render if amount hasn't changed
    const amountKey = `${packageId}-${amount}`;
    if (lastRenderedAmountRef.current === amountKey && currentButtonsRef.current) {
      return;
    }

    // Clear previous buttons safely
    if (paypalContainerRef.current) {
      paypalContainerRef.current.innerHTML = '';
    }

    // Close previous button instance if exists
    if (currentButtonsRef.current && currentButtonsRef.current.close) {
      try {
        currentButtonsRef.current.close();
      } catch (e) {
        // Ignore close errors
      }
    }

    const handleSuccess = () => {
      setSuccess(true);
      setSelectedAmount(null);
      setCustomAmount('');
      setDonorName('');
      setMessage('');
      donorNameRef.current = '';
      messageRef.current = '';
      setTimeout(() => setSuccess(false), 5000);
    };

    try {
      const buttons = window.paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'donate',
          height: 45
        },
        createOrder: async () => {
          setLoading(true);
          setError('');
          try {
            const response = await fetch(`${API_URL}/api/donations/create-order`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                package_id: packageId,
                custom_amount: packageId === 'custom' ? amount : null,
                donor_name: donorNameRef.current || (user?.username) || 'Anonymous',
                message: messageRef.current || null,
                origin_url: window.location.origin
              })
            });

            if (!response.ok) {
              const errData = await response.json();
              throw new Error(errData.detail || 'Failed to create order');
            }

            const data = await response.json();
            return data.order_id;
          } catch (err) {
            setLoading(false);
            setError(err.message);
            throw err;
          }
        },
        onApprove: async (data) => {
          try {
            const response = await fetch(`${API_URL}/api/donations/capture/${data.orderID}`, {
              method: 'POST',
              credentials: 'include'
            });

            if (response.ok) {
              handleSuccess();
            } else {
              setError('Payment capture failed');
            }
          } catch (err) {
            setError('Payment failed');
          }
          setLoading(false);
        },
        onError: (err) => {
          setLoading(false);
          setError('Payment error occurred. Please try again.');
          console.error('PayPal error:', err);
        },
        onCancel: () => {
          setLoading(false);
        }
      });

      // Render to container
      buttons.render(paypalContainerRef.current).then(() => {
        currentButtonsRef.current = buttons;
        lastRenderedAmountRef.current = amountKey;
      }).catch(err => {
        console.error('PayPal render error:', err);
        setError('Failed to load payment buttons');
      });
    } catch (err) {
      console.error('PayPal buttons init error:', err);
      setError('Failed to initialize payment');
    }
  }, [paypalLoaded, selectedAmount, customAmount, getAmountValue, user]);

  if (success) {
    return (
      <div className="glass rounded-2xl p-4" data-testid="donation-panel">
        <div className="flex flex-col items-center gap-3 py-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}>
            <CheckCircle className="w-8 h-8" style={{ color: '#22C55E' }} />
          </div>
          <h3 className="text-xl font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
            Thank You!
          </h3>
          <p className="text-sm text-center" style={{ color: 'var(--text-muted)' }}>
            Your donation helps keep the music playing!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl p-4" data-testid="donation-panel">
      <div className="flex items-center gap-2 mb-4">
        <Heart className="w-5 h-5" style={{ color: '#FF6B6B' }} />
        <h3 className="text-lg font-bold" style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}>
          Support The Station
        </h3>
      </div>

      <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
        Help keep the music playing! Your donation supports independent radio.
      </p>

      {/* Preset amounts */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        {AMOUNTS.map((amt) => {
          const Icon = amt.icon;
          const isSelected = selectedAmount === amt.id;
          return (
            <button
              key={amt.id}
              onClick={() => {
                setSelectedAmount(amt.id);
                setCustomAmount('');
              }}
              className="flex flex-col items-center gap-1 p-2 rounded-xl transition-all hover:scale-105"
              style={{ 
                backgroundColor: isSelected ? 'var(--primary)' : 'var(--surface-highlight)',
                color: isSelected ? 'white' : 'var(--text-main)'
              }}
              data-testid={`donate-${amt.value}`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-bold">{amt.label}</span>
            </button>
          );
        })}
      </div>

      {/* Custom amount */}
      <div className="mb-3">
        <div 
          className="flex items-center gap-2 p-3 rounded-xl"
          style={{ 
            backgroundColor: selectedAmount === 'custom' ? 'var(--primary)' : 'var(--surface-highlight)',
          }}
        >
          <DollarSign 
            className="w-4 h-4" 
            style={{ color: selectedAmount === 'custom' ? 'white' : 'var(--text-muted)' }} 
          />
          <input
            type="number"
            placeholder="Custom amount"
            value={customAmount}
            onChange={(e) => {
              setCustomAmount(e.target.value);
              setSelectedAmount('custom');
            }}
            onFocus={() => setSelectedAmount('custom')}
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: selectedAmount === 'custom' ? 'white' : 'var(--text-main)' }}
            min="1"
            max="1000"
            data-testid="donate-custom"
          />
        </div>
      </div>

      {/* Donor name (optional for anonymous) */}
      {!user && (
        <input
          type="text"
          placeholder="Your name (optional)"
          value={donorName}
          onChange={(e) => setDonorName(e.target.value)}
          className="w-full p-3 rounded-xl text-sm outline-none mb-3"
          style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
          data-testid="donor-name"
        />
      )}

      {/* Optional message toggle */}
      {!showMessage ? (
        <button
          type="button"
          onClick={() => setShowMessage(true)}
          className="flex items-center gap-1 text-xs mb-3 transition-colors"
          style={{ color: 'var(--text-muted)' }}
        >
          <MessageSquare className="w-3 h-3" />
          Add a message
        </button>
      ) : (
        <textarea
          placeholder="Message to the station (optional)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          className="w-full p-3 rounded-xl text-sm outline-none mb-3 resize-none"
          style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-main)' }}
          data-testid="donation-message"
        />
      )}

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-400 mb-3 text-center">{error}</p>
      )}

      {/* PayPal Buttons */}
      {getAmountValue() >= 1 && (
        <div className="mb-3">
          {loading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--primary)' }} />
            </div>
          )}
          <div 
            ref={paypalContainerRef} 
            style={{ display: loading ? 'none' : 'block', minHeight: '50px' }}
            data-testid="paypal-button-container"
          ></div>
        </div>
      )}

      {/* Amount display */}
      {getAmountValue() > 0 && (
        <p className="text-center text-sm mb-2" style={{ color: 'var(--text-main)' }}>
          Donating: <span className="font-bold" style={{ color: '#22C55E' }}>${getAmountValue().toFixed(2)}</span>
        </p>
      )}

      {/* Secure payment note */}
      <p className="text-xs text-center mt-3" style={{ color: 'var(--text-muted)' }}>
        <CreditCard className="w-3 h-3 inline mr-1" />
        Secure payment via PayPal
      </p>
    </div>
  );
}
